/**
 * Table Formatting Utilities
 *
 * Functions to detect and convert various table formats to proper Markdown tables.
 * Supports:
 * - Pipe-delimited tables (| col1 | col2 |)
 * - Tab-delimited tables
 * - Space-delimited tables (multiple spaces between columns)
 */

/**
 * Format text content, detecting and converting table-like structures to Markdown tables
 * @param {string} text - Text content to format
 * @returns {string} - Formatted text with proper Markdown tables
 */
/**
 * Detect if text contains well-formed GFM (GitHub Flavored Markdown) tables
 * GFM table pattern: header row, separator row with |---|, then data rows
 */
const hasWellFormedGFMTables = (text) => {
  // Pattern: | header | header |
  //          |--------|--------|  (or |---|---|)
  //          | data   | data   |
  const lines = text.split('\n');

  for (let i = 0; i < lines.length - 1; i++) {
    const currentLine = lines[i].trim();
    const nextLine = lines[i + 1]?.trim();

    // Check if current line looks like a table header (has pipes)
    if (currentLine.includes('|') && nextLine) {
      // Check if next line is a GFM table separator (|---|---|)
      // GFM separator has pipes and dashes with at least 3 dashes per column
      if (/^\|[\s-]+\|/.test(nextLine) && nextLine.split('|').length >= 3) {
        const parts = nextLine.split('|').filter(p => p.trim());
        // Each part should be mostly dashes (at least 3)
        const allDashes = parts.every(part => /^-{3,}$/.test(part.trim()));
        if (allDashes) return true;
      }
    }
  }
  return false;
};

export const formatTableContent = (text) => {
  if (!text) return text;

  // If text already contains well-formed GFM tables, return unchanged
  // This prevents breaking already-correct markdown that ReactMarkdown can parse
  if (hasWellFormedGFMTables(text)) {
    return text;
  }

  // Pre-process: Clean up malformed markdown tables (AI sometimes generates broken syntax)
  // Remove inline table separators like "| data | | --- | --- |" or standalone "| --- | --- |"
  text = text.replace(/\s*\|\s*[-\s|]+$/gm, '');

  // Remove standalone separator lines that look like "| --- | --- |" (but keep horizontal rules "---")
  let preLines = text.split('\n');
  const cleanedLines = preLines.filter(line => {
    const trimmed = line.trim();
    // Keep horizontal rules (exactly "---" or "___" or "***")
    if (trimmed === '---' || trimmed === '___' || trimmed === '***') return true;
    // Skip lines that look like table separators: "| --- | --- |" or similar
    return !/^\|\s*[-\s|]+\|\s*$/.test(trimmed);
  });
  text = cleanedLines.join('\n');

  // Function to detect if a line looks like a table row
  const isTableLike = (line) => {
    const trimmed = line.trim();

    // Empty lines are not tables
    if (!trimmed) return false;

    // Skip markdown table separator lines (e.g., "| --- | --- |" or "---")
    if (/^[\|\s\-]+$/.test(trimmed)) return false;

    // Check for common table indicators:
    // 1. Lines with pipes (at least 1 pipe for 2+ columns)
    // 2. Lines with tabs between columns
    // 3. Lines with multiple spaces for column separation (requires 3+ parts to avoid prose)
    const pipes = (line.match(/\|/g) || []).length;
    const hasTabs = line.includes('\t');
    const multipleSpaces = /\s{2,}/.test(line);

    // Pipe detection: at least 1 pipe suggests a table
    if (pipes >= 1) return true;

    // Tab detection: tabs are strong table indicators
    if (hasTabs) return true;

    // Space detection: require 3+ columns to avoid false positives on prose
    // e.g., "Hello  world" (2 parts) is prose, but "A  B  C" (3 parts) is likely a table
    if (multipleSpaces) {
      const parts = line.trim().split(/\s{2,}/).filter(p => p.length > 0);
      return parts.length >= 3;
    }

    return false;
  };

  // Process the text line by line
  const lines = text.split('\n');
  let result = [];
  let inTable = false;
  let tableLines = [];
  let hasHeader = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isCurrentLineTable = isTableLike(line);

    if (isCurrentLineTable && !inTable) {
      // Start of a table
      inTable = true;
      tableLines = [line];
      hasHeader = false;
    } else if (isCurrentLineTable && inTable) {
      // Continue table
      tableLines.push(line);
    } else if (!isCurrentLineTable && inTable) {
      // End of table - process it
      if (tableLines.length > 0) {
        const formattedTable = convertToMarkdownTable(tableLines, hasHeader);
        result.push(...formattedTable);
      }
      inTable = false;
      tableLines = [];
      result.push(line);
    } else {
      // Regular line
      result.push(line);
    }
  }

  // Process any remaining table
  if (inTable && tableLines.length > 0) {
    const formattedTable = convertToMarkdownTable(tableLines, false);
    result.push(...formattedTable);
  }

  return result.join('\n');
};

/**
 * Convert detected table lines to proper markdown table format
 * Note: Markdown tables always treat the first row as a header row,
 * so the hasHeader parameter is kept for API compatibility but not actively used.
 *
 * @param {string[]} lines - Array of table row lines
 * @param {boolean} hasHeader - (Deprecated) Not used - first row is always treated as header
 * @returns {string[]} - Array of formatted Markdown table lines
 */
export const convertToMarkdownTable = (lines, hasHeader = true) => {
  if (lines.length === 0) return [];

  // Parse cells from each line
  const rows = lines.map(line => {
    // Remove leading/trailing pipes if present
    let cleanLine = line.trim();
    if (cleanLine.startsWith('|')) cleanLine = cleanLine.substring(1);
    if (cleanLine.endsWith('|')) cleanLine = cleanLine.slice(0, -1);

    // Split by common delimiters
    let cells;
    if (cleanLine.includes('|')) {
      cells = cleanLine.split('|').map(cell => cell.trim());
    } else if (cleanLine.includes('\t')) {
      cells = cleanLine.split('\t').map(cell => cell.trim());
    } else {
      // Split by multiple spaces
      cells = cleanLine.split(/\s{2,}/).map(cell => cell.trim());
    }

    return cells;
  });

  // Find the maximum number of columns
  const maxColumns = Math.max(...rows.map(row => row.length));

  // Normalize all rows to have the same number of columns
  const normalizedRows = rows.map(row => {
    while (row.length < maxColumns) {
      row.push('');
    }
    return row;
  });

  // Build markdown table
  const result = [];

  // Add header row
  if (normalizedRows.length > 0) {
    result.push('| ' + normalizedRows[0].join(' | ') + ' |');

    // Add separator row
    const separator = normalizedRows[0].map(() => '---').join(' | ');
    result.push('| ' + separator + ' |');

    // Add data rows
    for (let i = 1; i < normalizedRows.length; i++) {
      result.push('| ' + normalizedRows[i].join(' | ') + ' |');
    }
  }

  return result;
};
